import { makeStyles } from '@material-ui/styles'
import CodeBlock from 'core/components/CodeBlock'
import Button from 'core/elements/button/Button'
import Modal from 'core/elements/modal/Modal'
import Theme from 'core/themes/model'
import React, { useEffect, useCallback } from 'react'
import { getVirtualMachine } from './actions'
import { trackEvent } from 'utils/tracking'

const useStyles = makeStyles<Theme>((theme) => ({
  errorBlock: {
    color: 'red',
    backgroundColor: '#fcebeb',
  },
}))

interface ErrorDialogProps {
  vmId: string
  hostName: string // Added hostName for the generic error message
  onClose: () => void
}

const VmErrorModal = ({ vmId, hostName, onClose }: ErrorDialogProps) => {
  const classes = useStyles()
  const [copied, setCopied] = React.useState(false)
  const [error, setError] = React.useState('')
  const [traceback, setTraceback] = React.useState('')

  useEffect(() => {
    const getVmCall = async () => {
      const response: any = await getVirtualMachine({ id: vmId })
      const fault = response?.fault

      if (!fault) {
        // Track empty fault event
        trackEvent('VM Fault Empty', { vmId, hostName })

        // Set generic error message
        setError(
          `This VM has encountered an unexpected error. Please check the nova service logs on "${hostName}" host for more details.`,
        )
        setTraceback('')
      } else {
        setError(fault?.message)
        setTraceback(fault?.details)
      }
    }
    getVmCall()
  }, [vmId, hostName])

  // Close on Esc
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const handleErrorCopy = useCallback(() => {
    navigator.clipboard.writeText(
      traceback
        ? `${error}

${traceback}`
        : `${error}`,
    )
    setCopied(true)
  }, [error, traceback])

  return (
    <Modal
      title="Error Details"
      onClose={onClose}
      maxWidth={1000}
      panel="dialog"
      footer={
        <>
          {traceback !== '' && (
            <Button variant="secondary" onClick={handleErrorCopy}>
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </>
      }
      open
    >
      <CodeBlock className={classes.errorBlock}>{error}</CodeBlock>
      {!!traceback && <CodeBlock className={classes.errorBlock}>{traceback}</CodeBlock>}
    </Modal>
  )
}

export default VmErrorModal
